import styles from './WelcomeModal.module.scss';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  VdsButton,
  VdsCheckbox,
  VdsDialog,
  VdsDialogActionFooter,
  VdsDialogBody,
  VdsDialogHeader,
  VdsLink,
} from '@volt/volt-extended-components-react';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { useTranslation } from 'react-i18next';
import useWelcomeModalSections from './useWelcomeModalSections';
import WelcomeModalSection, {
  WelcomeModalSectionData,
} from './components/welcome-modal-section/WelcomeModalSection';
import { setWelcomeModalSeen } from '@customer/store/slices/globalSlice';
import { ExternalUrl } from '@cas/config/links';
import { useHideWelcomeMutation } from '@customer/store/apis/authApi';

// This modal is only ever used once per page, so it should be safe to reuse the id across views.
const DIALOG_ID = 'welcome-modal';

export function WelcomeModal() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const refDialog = useRef<HTMLVdsDialogElement>(null);

  const { hideWelcomeDialog } = useAppSelector(state => state.auth);
  const { isWelcomeModalSeen } = useAppSelector(state => state.global);

  const sections = useWelcomeModalSections();

  const [isChecked, setIsChecked] = useState<boolean>(hideWelcomeDialog);

  const onCheckboxChange = useCallback(() => {
    setIsChecked(isChecked => !isChecked);
  }, []);

  const [postHideWelcome] = useHideWelcomeMutation();

  const onClose = useCallback(() => {
    if (isChecked && !hideWelcomeDialog) {
      // Post to the api if the hideWelcomeDialog user value has not yet been set to true.
      postHideWelcome();
    }

    // Set the isWelcomeModalSeen status in the redux store so that this modal is not automatically shown again in this session.
    // This must happen in the close function because we need to know through a user action that the content has been seen.
    dispatch(setWelcomeModalSeen());

    refDialog.current?.close();
  }, [dispatch, isChecked, postHideWelcome, hideWelcomeDialog]);

  // Conditionally open the dialog on mount
  useEffect(() => {
    if (!isWelcomeModalSeen && !hideWelcomeDialog) {
      refDialog.current?.open();
    }
  }, [dispatch, isWelcomeModalSeen, hideWelcomeDialog]);

  return (
    <VdsDialog
      id={DIALOG_ID}
      ref={refDialog}
      className={styles.dialog}
      padding="spacing-07"
      width="100%"
      maxWidth="var(--welcome-modal-max-width)"
      preventOutsideClose
    >
      <VdsDialogHeader onCloseButton={onClose}>
        <span className={styles.title}>{t('WelcomeTitle')}</span>
      </VdsDialogHeader>

      <VdsDialogBody>
        <div className={styles.dialogBody}>
          <div className={styles.accessDescription}>{t('WelcomeAccessDescription')}</div>
          <div className={styles.sections}>
            {sections.map((sectionProps: WelcomeModalSectionData, i) => {
              return (
                <Fragment key={i}>
                  {i > 0 && <div className={styles.divider} />}
                  <WelcomeModalSection
                    key={sectionProps.title}
                    {...sectionProps}
                    onClose={onClose}
                  />
                </Fragment>
              );
            })}
          </div>
          <div className={styles.contact}>
            <div className={styles.contact__description}>{t('WelcomeContactDescription')}</div>
            {/* Note: This is the only 'action' that should not close the modal because the action occurs in another browser tab. */}
            <VdsLink
              url={ExternalUrl.MiningContact}
              value={t('LetsTalk')}
              size="medium"
              underline
              target="_blank"
              rel="noopener noreferrer"
            />
          </div>
        </div>
      </VdsDialogBody>

      <VdsDialogActionFooter className={styles.footer} onCloseButton={onClose}>
        <div className={styles.checkbox}>
          <VdsCheckbox
            id="hide-welcome-modal"
            labelValue=""
            size="medium"
            checked={isChecked}
            onCheckboxChange={onCheckboxChange}
            // Note: The api/me/hidewelcome endpoint is a one way street, so the checkbox should be disabled from further editing.
            disabled={hideWelcomeDialog}
          />
          <div className={styles.checkbox__label}>{t('Do not show this message again')}</div>
        </div>
        <div className={styles.buttonContainer}>
          <VdsButton variant="secondary" close-dialog isFullWidth>
            {t('Close')}
          </VdsButton>
        </div>
      </VdsDialogActionFooter>
    </VdsDialog>
  );
}

export default WelcomeModal;
